import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { errorMessage } from '@/api/errors';
import { Button, type Accent } from './Button';
import { UnpluggedArt, DropArt } from '@/illustrations';

export function EmptyState({
  art,
  title,
  body,
  action,
  className,
  testId,
}: {
  art?: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <div data-testid={testId} className={cn('flex flex-col items-center rounded-card bg-surface px-6 py-10 text-center', className)}>
      {art && <div className="mb-5">{art}</div>}
      <h3 className="text-[19px] font-semibold">{title}</h3>
      {body && <p className="mt-1.5 max-w-sm text-[15px] text-muted">{body}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function ErrorState({
  error,
  title = 'That didn’t load',
  onRetry,
  className,
  compact,
}: {
  error?: unknown;
  title?: string;
  onRetry?: () => void;
  className?: string;
  compact?: boolean;
}) {
  return (
    <div role="alert" className={cn('flex flex-col items-center rounded-card bg-surface px-6 text-center', compact ? 'py-6' : 'py-10', className)}>
      {!compact && <UnpluggedArt className="mb-4 h-20 w-40 text-muted" />}
      <h3 className="text-[17px] font-semibold">{title}</h3>
      <p className="mt-1.5 max-w-sm text-sm text-muted">{errorMessage(error)}</p>
      {onRetry && (
        <Button variant="outline" accent="paper" size="sm" className="mt-5" onClick={onRetry} icon="flip">
          Try again
        </Button>
      )}
    </div>
  );
}

export function FeatureDisabled({ title, body, action }: { title: string; body: string; /** Ignored since v11. */ accent?: Accent; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center rounded-card bg-surface px-6 py-12 text-center" data-testid="feature-disabled">
      <DropArt className="mb-6 h-28 w-44" />
      <h3 className="font-display text-[32px] leading-[0.95]">{title}</h3>
      <p className="mt-2 max-w-md text-[15px] text-muted">Coming soon. {body}</p>
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function FullScreenLoader({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-ground" role="status" aria-label={label}>
      <div className="relative h-16 w-16">
        <span className="absolute inset-2 rounded-full bg-red motion-safe:animate-pulse" />
      </div>
    </div>
  );
}
